import { IonButton, IonIcon } from '@ionic/react';
import { settings, close } from 'ionicons/icons';
import { useState } from 'react';
import { Quiz } from '@/types/quiz';
import { Game, GameScenario, GameChoice } from '@/types/lessonGame';

interface TestPanelLessonPageProps {
  lessonType: 'video' | 'quiz' | 'game' | null;
  quiz?: Quiz | null;
  game?: Game | null;
}

export const TestPanelLessonPage: React.FC<TestPanelLessonPageProps> = ({
  lessonType,
  quiz,
  game
}) => {
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);

  // Only show for quiz or game lessons
  const shouldShow = lessonType === 'quiz' || lessonType === 'game';

  if (!shouldShow) {
    return null;
  }

  // Get correct answers based on lesson type
  const getCorrectAnswers = (): Array<{ number: number; answer: string }> => {
    if (lessonType === 'quiz' && quiz) {
      return quiz.quizQuestions
        .sort((a, b) => a.number - b.number)
        .map(q => ({
          number: q.number,
          answer: q.correctOption
        }));
    }

    if (lessonType === 'game' && game) {
      return game.scenarios
        .sort((a: GameScenario, b: GameScenario) => a.scenario_number - b.scenario_number)
        .map((s: GameScenario) => {
          // Find the best choice (Excellent first, then Good)
          const bestChoice = s.choices.find((c: GameChoice) => c.quality_level === 'Excellent') ||
                           s.choices.find((c: GameChoice) => c.quality_level === 'Good');
          return {
            number: s.scenario_number,
            answer: bestChoice?.letter || 'A'
          };
        });
    }

    return [];
  };

  const correctAnswers = getCorrectAnswers();

  // Split answers into two columns
  const midPoint = Math.ceil(correctAnswers.length / 2);
  const leftColumn = correctAnswers.slice(0, midPoint);
  const rightColumn = correctAnswers.slice(midPoint);

  // Show collapsed gear icon when not expanded
  if (!isPanelExpanded) {
    return (
      <div className="fixed bottom-4 left-4 z-50">
        <button
          onClick={() => setIsPanelExpanded(true)}
          className="opacity-30 hover:opacity-60 transition-opacity"
          aria-label="Open Test Panel"
        >
          <IonIcon icon={settings} style={{ fontSize: '16px' }} className="text-gray-500" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div className="bg-gray-100 rounded-lg p-4 shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-md font-semibold">Test Panel</h3>
          <button
            onClick={() => setIsPanelExpanded(false)}
            className="w-6 h-6 hover:bg-gray-300 rounded flex items-center justify-center transition-colors"
            aria-label="Close Test Panel"
          >
            <IonIcon icon={close} className="text-gray-700" />
          </button>
        </div>

        {/* Correct Answer Toggle */}
        <div className="mb-3">
          <IonButton
            onClick={() => setShowCorrectAnswers(!showCorrectAnswers)}
            color="secondary"
            fill={showCorrectAnswers ? 'solid' : 'outline'}
            size="small"
          >
            Correct Answers
          </IonButton>
        </div>

        {/* Correct Answers Display */}
        {showCorrectAnswers && (
          <div className="bg-white p-3 rounded border border-gray-300">
            <div className="text-xs font-semibold mb-2 text-gray-700">
              {lessonType === 'quiz' ? 'Quiz Answers' : 'Game Best Choices'}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
              {/* Left Column */}
              <div>
                {leftColumn.map(item => (
                  <div key={item.number}>
                    {item.number}. {item.answer}
                  </div>
                ))}
              </div>
              {/* Right Column */}
              <div>
                {rightColumn.map(item => (
                  <div key={item.number}>
                    {item.number}. {item.answer}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
